import { Col, Divider, Row } from 'antd';
import React, { FC, useContext, useEffect, useState } from 'react';
import FieldsRow from 'shared/components/FieldsRow';
import styles from './category5.module.scss';
import {
  Control,
  FieldValues,
  useFieldArray,
  useFormContext,
  useWatch,
  UseFormTrigger,
} from 'react-hook-form';
import {
  ScopeThreeData,
  ScopeWasteType,
} from 'models/ActivityDataEntryScopes/scopeThree.model';
import { ScopeData } from 'models/ActivityDataEntryScopes/scopes.model';
import { getWasteTypeFieldsData, getWeightsData } from './category5LayoutData';
import { queryParamsList } from 'views/ActivityDataEntry';
import { HelpDeskTooltipContext } from 'context/HelpDeskTooltipContext';
import { HelpDeskInputFieldEnum } from 'context/HelpDeskTooltipContext/helpDeskInputFieldEnum';
import ActivityDataEntryService from 'services/ActivityDataEntryService';
import { ReactComponent as AddIcon } from 'assets/icons/add_enclosed.svg';
import useFilters from 'shared/hooks/useFilters';
import { useScopeThreeFormEdit } from 'context/ScopeThreeFormEditContext';
import WasteDiversionRateCard from './WasteDiversionRateCard';
import SwitchToMonthlyModal from 'views/ActivityDataEntry/SwitchToMonthlyModal';
import SwitchAlertOfMonthlyDataModal from 'views/ActivityDataEntry/SwitchAlertOfMonthlyDataModal';
import {
  formatMonthlyTotal,
  isYearlyWasteRow,
  mapWasteRowToScopeData,
  sumMonthlyValues,
  toCalendarMonthlySlots,
} from './category5MonthlyUtils';

interface Category5LayoutProps {
  control: Control<ScopeThreeData>;
  trigger?: UseFormTrigger<FieldValues>;
}

const Category5Layout: FC<Category5LayoutProps> = (
  props: Category5LayoutProps
) => {
  const { control, trigger } = props;
  const { setValue, getValues } = useFormContext<ScopeThreeData>();
  const { isEditView } = useScopeThreeFormEdit();
  const readOnly = !isEditView;

  const { openTooltipModal } = useContext(HelpDeskTooltipContext);
  const { fetchWasteTypeMetaData, wasteTypeListMeta } =
    ActivityDataEntryService();
  const { getParams } = useFilters();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'scopeWasteType',
  });
  const wasteDiversionRate = useWatch({
    control,
    name: 'wasteDiversionRate',
  });
  const scopeWasteTypeValues = useWatch({
    control,
    name: 'scopeWasteType',
  });
  const { isProcessingFacility: isPF } = getParams(queryParamsList);
  const isProcessingFacility = isPF !== 'true';

  const [isSwitchMonthly, setIsSwitchMonthly] = useState(false);
  const [isSwitchMonthlyAlert, setIsSwitchMonthlyAlert] = useState(false);
  const [selectedWasteIndex, setSelectedWasteIndex] = useState<number>();
  const [initialMonthlyValues, setInitialMonthlyValues] = useState<ScopeData>(
    new ScopeData()
  );
  const [switchMonthlyItem, setSwitchMonthlyItem] = useState<ScopeData>(
    new ScopeData()
  );

  useEffect(
    () => {
      fetchWasteTypeMetaData();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- waste-type meta bootstrap
    []
  );

  const appendWasteType = () => {
    if (readOnly) return;
    append({ ...new ScopeWasteType() });
  };

  const handleToolTipOpen = (fieldName: HelpDeskInputFieldEnum) => () =>
    openTooltipModal(fieldName);

  const handleChange = async () => await trigger?.();

  const snapshotRowForModal = (index: number, clearMonthly?: boolean) => {
    const row = getValues(`scopeWasteType.${index}`);
    const modalValues = mapWasteRowToScopeData(
      row,
      wasteTypeListMeta,
      clearMonthly
    );
    setSelectedWasteIndex(index);
    setSwitchMonthlyItem(modalValues);
    setInitialMonthlyValues(modalValues);
    return row;
  };

  const handleSwitch = (index: number) => () => {
    const row = snapshotRowForModal(index, isYearlyWasteRow(getValues(`scopeWasteType.${index}`)));
    if (row?.totalWasteProduced) {
      setIsSwitchMonthlyAlert(true);
    } else {
      setIsSwitchMonthly(true);
    }
  };

  const handleMonthlyModalOpen = (index: number) => () => {
    snapshotRowForModal(index, false);
    setIsSwitchMonthly(true);
  };

  const handleModalSubmit = (values?: ScopeData) => {
    if (selectedWasteIndex == null) return;
    const totalValue = sumMonthlyValues(values?.scopeMonthlyData);
    setValue(
      `scopeWasteType.${selectedWasteIndex}.isYearly`,
      false
    );
    setValue(
      `scopeWasteType.${selectedWasteIndex}.totalWasteProduced`,
      formatMonthlyTotal(totalValue) as number
    );
    setValue(
      `scopeWasteType.${selectedWasteIndex}.scopeMonthlyData`,
      toCalendarMonthlySlots(values?.scopeMonthlyData)
    );
    trigger?.();
    setIsSwitchMonthly(false);
  };

  const handleAlertSubmit = () => {
    if (selectedWasteIndex == null) return;
    const row = getValues(`scopeWasteType.${selectedWasteIndex}`);
    if (isYearlyWasteRow(row)) {
      setInitialMonthlyValues(
        mapWasteRowToScopeData(row, wasteTypeListMeta, true)
      );
      setIsSwitchMonthly(true);
    } else {
      setValue(`scopeWasteType.${selectedWasteIndex}.isYearly`, true);
      setValue(`scopeWasteType.${selectedWasteIndex}.scopeMonthlyData`, null);
      trigger?.();
    }
    setIsSwitchMonthlyAlert(false);
  };

  const weightFieldsData = getWeightsData({
    handleToolTipOpen,
    isProcessingFacility,
    handleChange,
    readOnly,
  });
  return (
    <Row>
      <Col span={22}>
        <Row>
          <Col span={22}>
            <WasteDiversionRateCard rate={wasteDiversionRate} />
          </Col>
          <Col span={22}>
            <Divider />
          </Col>
          <Col span={24}>
            <FieldsRow {...weightFieldsData} />
          </Col>
          <Col span={22}>
            <Divider />
          </Col>
          <Col span={22} className={styles.wasteTypeHeader}>
            <Row align="middle" justify="space-between">
              <Col className={styles.wasteTypeTitle}>Waste Type</Col>
              {!readOnly && (
                <div
                  className={styles.addWasteType}
                  onClick={appendWasteType}>
                  <Col className={styles.addIcon}>
                    <AddIcon />
                  </Col>
                  <Col>Waste type</Col>
                </div>
              )}
            </Row>
          </Col>
          {fields?.map((field, index) => (
            <Col span={24} className={styles.fieldsRow} key={field?.id}>
              <FieldsRow
                {...getWasteTypeFieldsData({
                  index,
                  handleToolTipOpen,
                  remove,
                  arrayLength: fields?.length,
                  wasteTypeListMeta,
                  handleChange,
                  readOnly,
                  isYearly: isYearlyWasteRow(scopeWasteTypeValues?.[index]),
                  onSwitch: handleSwitch(index),
                  onViewMonthly: handleMonthlyModalOpen(index),
                })}
              />
            </Col>
          ))}
        </Row>
      </Col>
      <SwitchToMonthlyModal
        isModalOpen={isSwitchMonthly}
        setIsModalOpen={setIsSwitchMonthly}
        initialValues={initialMonthlyValues}
        handleSubmit={handleModalSubmit}
      />
      <SwitchAlertOfMonthlyDataModal
        isModalOpen={isSwitchMonthlyAlert}
        setIsModalOpen={setIsSwitchMonthlyAlert}
        handleSubmit={handleAlertSubmit}
        sourceValue={switchMonthlyItem}
      />
    </Row>
  );
};

export default Category5Layout;
